<template>
  <!-- Modal backdrop -->
  <Transition name="modal-backdrop">
    <div
      v-if="modelValue"
      class="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1000] transition-opacity duration-200"
      @click="handleBackdropClick"
    ></div>
  </Transition>

  <!-- Modal container -->
  <Transition name="modal">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      @click.self="handleBackdropClick"
    >
      <div :class="modalClasses" @click.stop>
        <!-- Modal Header -->
        <div v-if="hasHeader" class="modal-header flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <slot name="header">
            <h3 class="text-base font-semibold text-gray-900">{{ title }}</h3>
          </slot>
          <BaseButton
            v-if="closable"
            type="button"
            variant="icon"
            size="sm"
            class="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none ml-4"
            aria-label="Fermer"
            @click="handleClose"
          >
            <i class="fas fa-times text-sm"></i>
          </BaseButton>
        </div>

        <!-- Modal Body -->
        <div :class="bodyClasses" class="modal-body flex-1 overflow-y-auto">
          <slot></slot>
        </div>

        <!-- Modal Footer -->
        <div v-if="hasFooter" class="modal-footer flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <slot name="footer">
            <BaseButton
              v-if="showCancelButton"
              variant="ghost"
              @click="handleCancel"
            >
              {{ cancelText }}
            </BaseButton>
            <BaseButton
              v-if="showConfirmButton"
              :variant="confirmVariant"
              :loading="confirmLoading"
              @click="handleConfirm"
            >
              {{ confirmText }}
            </BaseButton>
          </slot>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import BaseButton from './BaseButton.vue'

export default {
  name: 'BaseModal',
  components: {
    BaseButton
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', 'full'].includes(value)
    },
    closable: {
      type: Boolean,
      default: true
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    showCancelButton: {
      type: Boolean,
      default: false
    },
    showConfirmButton: {
      type: Boolean,
      default: false
    },
    cancelText: {
      type: String,
      default: 'Annuler'
    },
    confirmText: {
      type: String,
      default: 'Confirmer'
    },
    confirmVariant: {
      type: String,
      default: 'primary'
    },
    confirmLoading: {
      type: Boolean,
      default: false
    },
    padding: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl'].includes(value)
    }
  },
  computed: {
    hasHeader() {
      return this.$slots.header || this.title
    },
    hasFooter() {
      return this.$slots.footer || this.showCancelButton || this.showConfirmButton
    },
    modalClasses() {
      return [
        'modal-container bg-white rounded-2xl border border-gray-200 transform transition-all w-full flex flex-col',
        this.sizeClasses
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
        '4xl': 'max-w-4xl',
        '5xl': 'max-w-5xl',
        '6xl': 'max-w-6xl',
        full: 'max-w-full mx-4'
      }
      return sizes[this.size]
    },
    bodyClasses() {
      const paddings = {
        none: 'p-0',
        sm: 'px-5 py-3',
        md: 'px-5 py-4',
        lg: 'px-5 py-5',
        xl: 'px-5 py-6'
      }
      return paddings[this.padding]
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:modelValue', false)
      this.$emit('close')
    },
    handleBackdropClick() {
      if (this.closeOnBackdrop) {
        this.handleClose()
      }
    },
    handleCancel() {
      this.$emit('cancel')
      this.handleClose()
    },
    handleConfirm() {
      this.$emit('confirm')
    }
  },
  watch: {
    modelValue(newValue) {
      if (newValue) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
/* Modal container */
.modal-container {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  flex-shrink: 0;
}

/* Body - scrollable */
.modal-body {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Custom scrollbar */
.modal-body::-webkit-scrollbar {
  width: 4px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #94A3B8;
}

/* Footer */
.modal-footer {
  flex-shrink: 0;
}

/* Modal transitions */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}
</style>
